export const DRAW_PAN_SLOP_PX = 10;
export const SYNTHETIC_CLICK_SUPPRESSION_MS = 700;
export const SYNTHETIC_CLICK_RADIUS_PX = 32;

export type DrawGestureTarget = "canvas" | "vertex" | "midpoint" | "control" | "passive";
export type DrawGesturePhase = "idle" | "tapCandidate" | "panning" | "vertexDragging" | "cancelled";

export interface DrawGestureState {
  phase: DrawGesturePhase;
  touchId: number | null;
  target: DrawGestureTarget;
  startX: number;
  startY: number;
  startedAt: number;
  suppressClickUntil: number;
  suppressionX: number | null;
  suppressionY: number | null;
  syntheticClickPending: boolean;
}

export const idleDrawGesture = (): DrawGestureState => ({
  phase: "idle",
  touchId: null,
  target: "canvas",
  startX: 0,
  startY: 0,
  startedAt: 0,
  suppressClickUntil: 0,
  suppressionX: null,
  suppressionY: null,
  syntheticClickPending: false,
});

export function beginDrawGesture(input: {
  touchId: number;
  target: DrawGestureTarget;
  clientX: number;
  clientY: number;
  now: number;
  isMultiTouch?: boolean;
}): DrawGestureState {
  if (input.isMultiTouch) {
    return {
      ...idleDrawGesture(),
      phase: "cancelled",
      suppressClickUntil: input.now + SYNTHETIC_CLICK_SUPPRESSION_MS,
      suppressionX: input.clientX,
      suppressionY: input.clientY,
      syntheticClickPending: true,
    };
  }

  return {
    phase: input.target === "vertex" || input.target === "midpoint" ? "vertexDragging" : input.target === "canvas" ? "tapCandidate" : "idle",
    touchId: input.touchId,
    target: input.target,
    startX: input.clientX,
    startY: input.clientY,
    startedAt: input.now,
    suppressClickUntil: 0,
  };
}

export function updateDrawGesture(
  state: DrawGestureState,
  input: { touchId: number; clientX: number; clientY: number; now: number },
): DrawGestureState {
  if (state.touchId !== input.touchId || state.phase !== "tapCandidate") return state;
  const distance = Math.hypot(input.clientX - state.startX, input.clientY - state.startY);
  if (distance < DRAW_PAN_SLOP_PX) return state;

  return {
    ...state,
    phase: "panning",
    suppressClickUntil: input.now + SYNTHETIC_CLICK_SUPPRESSION_MS,
    suppressionX: input.clientX,
    suppressionY: input.clientY,
    syntheticClickPending: true,
  };
}

export function finishDrawGesture(
  state: DrawGestureState,
  input: { touchId: number; clientX: number; clientY: number; now: number; cancelled?: boolean },
): DrawGestureState {
  if (state.touchId !== input.touchId) return state;
  const shouldSuppress = input.cancelled || state.phase === "panning" || state.phase === "cancelled";
  return {
    ...idleDrawGesture(),
    suppressClickUntil: shouldSuppress ? Math.max(state.suppressClickUntil, input.now + SYNTHETIC_CLICK_SUPPRESSION_MS) : 0,
    suppressionX: shouldSuppress ? input.clientX : null,
    suppressionY: shouldSuppress ? input.clientY : null,
    syntheticClickPending: shouldSuppress,
  };
}

export function shouldSuppressSyntheticClick(
  state: DrawGestureState,
  input: { now: number; clientX: number; clientY: number },
): boolean {
  if (!state.syntheticClickPending || input.now > state.suppressClickUntil) return false;
  if (state.suppressionX === null || state.suppressionY === null) return false;
  return Math.hypot(input.clientX - state.suppressionX, input.clientY - state.suppressionY) <= SYNTHETIC_CLICK_RADIUS_PX;
}

export function consumeSyntheticClickSuppression(
  state: DrawGestureState,
  input: { now: number; clientX: number; clientY: number },
): { state: DrawGestureState; suppressed: boolean } {
  const suppressed = shouldSuppressSyntheticClick(state, input);
  if (!suppressed) return { state, suppressed: false };
  return {
    state: { ...state, syntheticClickPending: false, suppressClickUntil: 0, suppressionX: null, suppressionY: null },
    suppressed: true,
  };
}

export function shouldAcceptDrawingVertex(state: DrawGestureState): boolean {
  return state.phase !== "panning" && state.phase !== "cancelled";
}

export function shouldClearTransientDrawingFeedback(state: DrawGestureState): boolean {
  return state.phase === "panning" || state.phase === "vertexDragging" || state.phase === "cancelled";
}
